import { Station } from '@/types'

export const COLUMN_STATE_KEY = 'stationGridColumnState'
export const COLUMN_VISIBILITY_KEY = 'stationGridVisibleColumns'

export const SIMPLE_TEXT_FILTER_PARAMS = {
  filterOptions: ['contains', 'startsWith'],
  defaultOption: 'contains',
}

export const SIMPLE_SET_FILTER_PARAMS = {
  buttons: ['reset', 'apply'],
  closeOnApply: true,
}

export const CONTAINS_ONLY_FILTER_PARAMS = {
  filterOptions: ['contains'],
  defaultOption: 'contains',
}

export interface StationTableViewProps {
  data?: Station[]
  totalCount?: number
  // Guided setup (SCRUM-1032) embeds this grid scoped to an explicit interface.
  // When provided, it overrides the globally-selected interface from AuthContext
  // so every fetch/save/delete targets this controller (SCRUM-976). Config leaves
  // it undefined and keeps using the AuthContext interface.
  interfaceId?: number | string
  // External-data mode (used by the Map View): when provided, the grid renders
  // exactly these rows (already filtered to the stations visible on the map)
  // and skips its own infinite-scroll fetch + SignalR. Config leaves it
  // undefined and keeps the internal paginated query.
  stations?: any[]
  onEditItem: (id: string) => void
  onDeleteItem: (id: string, name?: string) => void
  onViewItem: (id: string) => void
  onLoadMore?: () => void
  isLoadingMore?: boolean
  bulkEditMode?: boolean
  onToggleBulkEdit?: () => void
  onOpenColumnVisibility?: (event: React.MouseEvent<HTMLButtonElement>) => void
  keyword?: string
  onViewSaved?: (name: string, columns: Record<string, boolean>) => void
  onSelectedRowsChanged?: (ids: string[]) => void
  /**
   * Opens the list with one draft row per Irrigation / Non-Irrigation decoder
   * that has no station yet (SCRUM-1040). Guided setup only: on /config an
   * empty station list is a fact about the site, not a step to get past.
   */
  draftFromDecoders?: boolean
  /**
   * How far the writing has got, for the page's toolbar readout: the guided
   * autosave, and on /config the bulk save (SCRUM-1010 QC item 2). `null`
   * clears it when nothing is being written.
   */
  onProgressChange?: (progress: {
    saved: number
    pending: number
    /**
     * Every station this controller should end up with — one per station
     * decoder. Fixed for the sitting, so a progress readout against it does
     * not move as the work is done.
     */
    total: number
    /** Seconds left at the rate the rows are going through, or null. */
    etaSeconds?: number | null
    /** Rows flagged by the decoders or refused by the API. */
    issues: number
    issuesOnly: boolean
    onToggleIssuesOnly: () => void
    /** Rows still missing something the save needs — what is left to do. */
    incomplete: number
    incompleteOnly: boolean
    onToggleIncompleteOnly: () => void
    /** Hides the stations already written, leaving what is still to do. */
    hideSaved: boolean
    onToggleHideSaved: () => void
    saving: boolean
    /**
     * Rows this pass is writing, as opposed to `saved`/`total`, which are the
     * figures for the whole list. Re-editing two rows of a finished list read
     * as "Saving… 479 of 479" — true about the list, silent about the pass, and
     * indistinguishable from a stuck save (SCRUM-1032 QC round 6, item 3).
     */
    inFlight?: number
    failed: number
    /** What the server said about the first failure, if any. */
    error?: string | null
    /**
     * Why an edit to a saved station was not written: it emptied a required
     * field (SCRUM-1032 QC round 7). Null when nothing is held back.
     */
    refused?: string | null
    repurposed: number
    typeMismatch: number
    duplicated: number
  } | null) => void
  /**
   * The "still to fill in" filter, for a toolbar outside the grid. Reported
   * everywhere the grid is used, so /config can offer it too — there the list
   * pages, so `known` is false until the whole list is in hand and the page
   * shows the control without a number.
   */
  onIncompleteFilterChange?: (state: {
    count: number
    known: boolean
    only: boolean
    onToggle: () => void
  }) => void
}

export interface LOCATION_VIEW {
  id: number
  interfaceId: number
  name: string
  numOfFloorOverlays: number
  numOfFloors: number
  siteId: number
}

export const STATION_TYPE_OPTIONS = [
  { label: 'stationsTypeNonIrrigation', value: 1 },
  { label: 'stationsTypeIrrigation', value: 2 },
]

export const DECODER_TYPES = [
  'Pump Start',
  'Filtration',
  'Fertigation',
  'ML1',
  'Irrigation',
  'Agitator',
  'Flushing',
]

// Basic List view: only show these columns
export const BASIC_LIST_FIELDS = [
  'description',
  'decoderAddress',
  'pipe',
  'priority',
  'area',
  'subArea',
  'state',
  'plant',
  'emitter',
  'stationType',
  'viewAction',
]

// Always kept visible by a preset (row identity + the View/Action column). The
// bulk-only EDIT/DELETE column is driven by bulk-edit mode, not by presets.
export const PRESET_ALWAYS_FIELDS = ['viewDetail', 'description']

// Column presets — show/hide groups of related columns in one click. Each
// preset shows its own fields plus PRESET_ALWAYS_FIELDS and hides the rest.
export const COLUMN_PRESETS: { name: string; fields: string[] }[] = [
  {
    // Everything except Station Photo and the Irrigation group
    // (priority, defaultRunTime, waterRequired, etZone).
    name: 'Basic List',
    fields: [
      'decoderAddress',
      'state',
      'stationType',
      'area',
      'subArea',
      'plant',
      'soil',
      'emitter',
      'pipe',
      'expectedFlow',
      'precipitationRate',
    ],
  },
  {
    name: 'Full Details',
    fields: [
      'decoderAddress',
      'state',
      'stationType',
      'area',
      'subArea',
      'plant',
      'soil',
      'emitter',
      'pipe',
      'expectedFlow',
      'precipitationRate',
      'priority',
      'defaultRunTime',
      'waterRequired',
      'etZone',
    ],
  },
]

// Column header groups. Listed fields render under a
// shared group header; fields not listed here render as standalone columns.
// Each group's children must appear consecutively in COLUMN_CONFIG.
export const COLUMN_GROUPS: {
  groupId: string
  headerName: string
  children: string[]
}[] = [
  {
    groupId: 'stationInfo',
    headerName: 'stationsGroupStationInfo',
    children: ['description', 'decoderAddress', 'state'],
  },
  {
    groupId: 'configuration',
    headerName: 'stationsGroupConfiguration',
    children: ['stationType', 'area', 'subArea', 'plant', 'soil', 'emitter'],
  },
  {
    groupId: 'flow',
    headerName: 'stationsGroupFlow',
    children: ['pipe', 'expectedFlow', 'precipitationRate'],
  },
  {
    groupId: 'irrigation',
    headerName: 'stationsGroupIrrigation',
    children: ['priority', 'defaultRunTime', 'waterRequired', 'etZone'],
  },
]

export const COLUMN_CONFIG = [
  {
    field: 'viewDetail',
    headerName: '',
    minWidth: 56,
    maxWidth: 56,
    sortable: false,
    filter: false,
    editable: false,
    valueType: 'viewDetail',
    suppressMovable: true,
    pinned: 'left',
    lockPosition: 'left',
  },
  {
    field: 'description',
    headerName: 'stationsColumnStationName',
    minWidth: 130,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    // Editable since the table was written; it was turned off in 54ddd811a
    // inside a hunk that was otherwise swapping this column's filter from
    // text to set, with nothing in the message about making the name
    // read-only. The save has carried `description` throughout, and the
    // column has stayed flagged mandatory — which only means anything for a
    // field someone can fill in.
    editable: true,
    mandatory: true,
    valueType: 'text',
  },
  {
    field: 'decoderAddress',
    headerName: 'stationsColumnDecoderAddress',
    minWidth: 120,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    mandatory: false,
    hasCustomRenderer: true,
    valueType: 'richSelect',
  },
  {
    field: 'state',
    headerName: 'stationsColumnStatus',
    minWidth: 150,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    editable: false,
    valueType: 'status',
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    cellFormatting: {
      idle: '#7A8090',
      running: '#12B5C5',
      stated: '#27ff06ff',
      error: '#FF4F5E',
    },
  },
  {
    field: 'stationType',
    headerName: 'stationsColumnType',
    // "Non Irrigation" as a chip is ~96px, and the dropdown caret takes 26 more
    // — at 120 the label had to ellipsise to "Non Irrig…".
    minWidth: 145,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    hasCustomFormatter: true,
    valueType: 'richSelect',
  },
  {
    field: 'area',
    headerName: 'stationsColumnArea',
    minWidth: 120,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    mandatory: true,
    valueType: 'richSelect',
  },
  {
    field: 'subArea',
    headerName: 'stationsColumnSubArea',
    width: 120,
    minWidth: 120,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    mandatory: true,
    hasCustomFormatter: true,
    valueType: 'richSelect',
  },
  {
    field: 'plant',
    headerName: 'stationsColumnPlant',
    minWidth: 115,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    mandatory: true,
    valueType: 'richSelect',
  },
  {
    field: 'soil',
    headerName: 'stationsColumnSoil',
    minWidth: 105,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    mandatory: true,
    valueType: 'richSelect',
  },
  {
    field: 'emitter',
    headerName: 'stationsColumnEmitter',
    minWidth: 105,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    mandatory: true,
    valueType: 'richSelect',
  },
  {
    field: 'pipe',
    headerName: 'stationsColumnPipe',
    minWidth: 100,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    mandatory: true,
    hasCustomFormatter: true,
    valueType: 'richSelect',
  },
  {
    field: 'expectedFlow',
    headerName: 'stationsColumnExpectedFlow',
    minWidth: 115,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    editable: true,
    valueType: 'number',
    metricUnit: 'l/m',
    // Required to write an irrigation station, like the precipitation rate.
    mandatory: true,
  },
  {
    field: 'precipitationRate',
    headerName: 'stationsColumnPrecipitationRate',
    // Wide enough for "Precipitation Rate (mm/h) *" on two lines; at 115 the
    // unit and the required asterisk wrapped onto a clipped third line.
    minWidth: 135,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    editable: true,
    mandatory: true,
    valueType: 'number',
    metricUnit: 'mm/h',
  },
  {
    field: 'priority',
    headerName: 'stationsColumnPriority',
    // Same scale as a valve's priority (SCRUM-1079).
    headerTooltip: 'stationsTipPriority',
    minWidth: 110,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    editable: true,
    mandatory: true,
    valueType: 'number',
    numberRange: { min: 0, max: 255 },
    numberRangeMessage: 'formsValidationPriorityRange',
  },
  {
    field: 'defaultRunTime',
    headerName: 'stationsColumnDefaultRuntime',
    minWidth: 115,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    editable: true,
    valueType: 'runtime',
  },
  {
    field: 'waterRequired',
    headerName: 'stationsColumnDefaultWaterRequired',
    minWidth: 115,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    editable: true,
    valueType: 'number',
    metricUnit: 'mm',
  },
  {
    field: 'etZone',
    headerName: 'stationsColumnMicroClimate',
    minWidth: 120,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: false,
    filterParams: SIMPLE_SET_FILTER_PARAMS,
    editable: true,
    hasCustomFormatter: true,
    valueType: 'richSelect',
  },
  {
    /**
     * SCRUM-1010 QC, 21 Sep — not pinned.
     *
     * Pinned to the right it floated over the scrolling area, and since it is
     * the column after Micro Climate, those cells could never be scrolled out
     * from under it: "Microclimate column is blocked by delete column. delete
     * column can leave to the last column of the table" (Wei Woon).
     *
     * The cost is that delete, and the issue flag beside it, now have to be
     * scrolled to on a narrow window. The row's Status column carries the same
     * error, so nothing is only visible here.
     */
    field: 'viewAction',
    headerName: '',
    minWidth: 72,
    maxWidth: 72,
    sortable: false,
    filter: false,
    editable: false,
    valueType: 'action',
    suppressMovable: true,
  },
]
